import type { ElementType, HTMLAttributes, ReactNode } from 'react';
import useDetectedTruncation from '@hooks/useDetectTruncation';
import { twMerge } from 'tailwind-merge';

import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';

const baseClasses = 'truncate cursor-default';

interface BaseTooltipOnTruncatedProps<T extends HTMLElement> extends Omit<HTMLAttributes<T>, 'content'> {
  tooltipContent: ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  maxWidth?: number | string;
}

interface TooltipOnTruncatedGenericProps<T extends HTMLElement> extends BaseTooltipOnTruncatedProps<T> {
  as: ElementType;
}

function TooltipOnTruncatedGeneric<T extends HTMLElement>({
  as: Component,
  tooltipContent,
  side = 'top',
  maxWidth,
  children,
  className,
  ...props
}: TooltipOnTruncatedGenericProps<T>) {
  const { isTruncated, ref } = useDetectedTruncation<T>();

  const element = (
    <Component ref={ref} className={twMerge(baseClasses, className)} {...props}>
      {children}
    </Component>
  );

  if (!isTruncated) return element;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{element}</TooltipTrigger>
      <TooltipContent
        side={side}
        style={maxWidth ? { maxWidth: typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth } : undefined}
      >
        {tooltipContent}
      </TooltipContent>
    </Tooltip>
  );
}

export function TooltipOnTruncated(props: BaseTooltipOnTruncatedProps<HTMLSpanElement>) {
  return <TooltipOnTruncatedGeneric as="span" {...props} />;
}

export function TooltipOnTruncatedCell(props: BaseTooltipOnTruncatedProps<HTMLTableCellElement>) {
  return <TooltipOnTruncatedGeneric as="td" {...props} />;
}
